import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ClassRoundedIcon from '@mui/icons-material/ClassRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
}));

const TableCard = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const TableRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '180px 1fr 120px',
  gap: theme.spacing(2),
  alignItems: 'center',
  padding: theme.spacing(1.6, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export default function ClassesTagsView({ isAddDialogOpen, onOpenAddDialog, onCloseAddDialog }) {
  return {
    main: (
      <>
        <MainCard>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ClassRoundedIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 24, md: 30 } }}>
                  Classes & Tags
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  What is a class?
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Checkbox size="small" />
                  <Typography variant="body2" color="text.secondary">
                    Lock Classes
                  </Typography>
                </Stack>
                <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={onOpenAddDialog} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Add
                </Button>
                <Button variant="contained" startIcon={<EditRoundedIcon />} onClick={onOpenAddDialog} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Modify Classes
                </Button>
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
              <Stack spacing={1}>
                <Tabs value={0} sx={{ minHeight: 38 }}>
                  <Tab disableRipple label="Classes 2" sx={{ minHeight: 38, textTransform: 'none' }} />
                  <Tab disableRipple label="Tags 0" sx={{ minHeight: 38, textTransform: 'none' }} />
                </Tabs>
                <TextField
                  size="small"
                  placeholder="Search classes..."
                  sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Select size="small" defaultValue="asc" sx={{ minWidth: 210, borderRadius: 2 }}>
                <MenuItem value="asc">Sort By Class Ascending</MenuItem>
              </Select>
            </Stack>

            <TableCard>
              <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 120px', gap: 2, px: 2, py: 1.4, color: 'text.disabled', fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                <span>Color</span>
                <span>Class Name</span>
                <span>Count</span>
              </Box>
              <TableRow>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#7c3aed' }} />
                </Stack>
                <Typography variant="body2">cat</Typography>
                <Typography variant="body2" color="text.secondary">3</Typography>
              </TableRow>
              <TableRow>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#84cc16' }} />
                </Stack>
                <Typography variant="body2">person</Typography>
                <Typography variant="body2" color="text.secondary">1</Typography>
              </TableRow>
            </TableCard>
          </Stack>
        </MainCard>

        <Dialog open={isAddDialogOpen} onClose={onCloseAddDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AddRoundedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Add New Classes
              </Typography>
            </Stack>
            <IconButton size="small" onClick={onCloseAddDialog}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Add a comma separated list of class names
                </Typography>
                <TextField fullWidth placeholder="cat, dog, ..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Checkbox size="small" />
                  <Typography variant="body2" color="text.secondary">
                    Fix invalid classes
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Upload Classes CSV
                  </Button>
                  <Button variant="contained" sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Add Classes
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    ),
    side: null,
  };
}
